'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '@/providers/auth-provider';
import { cn } from '@/lib/utils';
import { LayoutDashboard, Users, Server, Database, DollarSign, BarChart3, Settings, ChevronLeft, ChevronRight, Menu, LogOut, User, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Providers', href: '/admin/providers', icon: Server },
  { name: 'Numbers', href: '/admin/numbers', icon: Database },
  { name: 'Settlements', href: '/admin/settlements', icon: DollarSign },
  { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-950">
        {/* Mobile sidebar overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <motion.aside
          initial={{ x: -280 }}
          animate={{ x: sidebarOpen ? 0 : collapsed ? -80 : 0 }}
          className={cn(
            'fixed left-0 top-0 z-50 h-full bg-slate-950/95 backdrop-blur-xl border-r border-slate-800/50',
            'transition-transform duration-300 ease-in-out',
            collapsed ? 'w-20' : 'w-72'
          )}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={cn('flex items-center justify-between h-16 px-4 border-b border-slate-800/50', collapsed && 'justify-center')}>
              <Link to="/admin" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                {!collapsed && (
                  <span className="font-bold text-lg text-white">IPRN Online</span>
                )}
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCollapsed(!collapsed)}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto" aria-label="Admin navigation">
              {navigation.map((item) => (
                <Tooltip key={item.name}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.href}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                        'text-slate-400 hover:text-white hover:bg-slate-800/50',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50',
                        collapsed && 'justify-center px-2'
                      )}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                      {!collapsed && <span className="truncate">{item.name}</span>}
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="glass-strong">
                    {item.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </nav>

            {/* Bottom section */}
            <div className={cn('p-4 border-t border-slate-800/50', collapsed && 'px-2')}>
              <Separator className="mb-4" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className={cn(
                          'w-full justify-start gap-3 px-3 py-2 rounded-xl',
                          'text-slate-400 hover:text-white hover:bg-slate-800/50',
                          collapsed && 'justify-center px-2'
                        )}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user?.email ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}` : undefined} alt={user?.email || 'User'} />
                          <AvatarFallback>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        {!collapsed && (
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                          </div>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="glass-strong w-48" align="end">
                      <div className="px-3 py-2 border-b border-slate-700/50">
                        <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                        <p className="text-xs text-slate-400">{user?.tier} tier</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin/profile" className="flex items-center gap-2 w-full">
                          <User className="h-4 w-4" />
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/settings" className="flex items-center gap-2 w-full">
                          <Settings className="h-4 w-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-300 flex items-center gap-2 w-full">
                        <LogOut className="h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TooltipTrigger>
                <TooltipContent side="right" className="glass-strong">
                  Account menu
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </motion.aside>

        {/* Main content */}
        <div className={cn('transition-all duration-300', collapsed ? 'lg:ml-20' : 'lg:ml-72')}>
          {/* Top bar */}
          <header className="sticky top-0 z-30 h-16 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
            <div className="flex items-center justify-between h-full px-6">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div className="flex-1 lg:flex lg:items-center lg:justify-between">
                <h1 className="text-xl font-bold text-white truncate">Admin Dashboard</h1>
                <div className="flex items-center gap-4">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/50 border border-slate-700/50">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
                    </span>
                    <span className="text-xs text-green-400">All Systems Operational</span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}